/**
 * Browser step-up: Google reauth then remint `__session` so `auth_time` is fresh.
 * Silent ID-token refresh does not change `auth_time` (ADR-0018).
 */

import { GoogleAuthProvider, reauthenticateWithPopup, reauthenticateWithRedirect } from 'firebase/auth';
import { auth, shouldUseRedirectAuth } from '@/lib/firebase-config';
import { completeLogin } from '@/lib/access/login-client';
import { setRedirectIntent } from '@/lib/auth-redirect-handler';
import logger from '@/lib/logging';

export const STEP_UP_RETURN_PATH_KEY = 'harry_step_up_return_path';

export async function reauthenticateForStepUp(): Promise<'redirect' | 'done'> {
  const currentUser = auth?.currentUser;
  if (!auth || !currentUser) {
    throw new Error('Not signed in');
  }

  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'login' });

  if (shouldUseRedirectAuth()) {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(
        STEP_UP_RETURN_PATH_KEY,
        `${window.location.pathname}${window.location.search}`,
      );
    }
    setRedirectIntent();
    await reauthenticateWithRedirect(auth, provider);
    return 'redirect';
  }

  await reauthenticateWithPopup(auth, provider);
  const idToken = await currentUser.getIdToken(true);
  const response = await completeLogin(idToken);
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    logger.error('[step-up] Session remint after reauth failed', {
      status: response.status,
      text: text.slice(0, 300),
    });
    throw new Error(text || `Session remint failed (${response.status})`);
  }
  return 'done';
}
